/**
 * Who an invoice went to, in people and roles (the new invoicing): shared by
 * the web and the phone app so both say "Accounting — Dana Ruiz".
 */
export const INVOICE_CONTACT_ROLE_LABELS = {
    main: 'Main',
    accounting: 'Accounting',
    service_manager: 'Service manager',
    sales_manager: 'Sales manager',
    other: 'Other',
};
/** The via-role of an invoice's own customer (Customers): they got the invoice made out to them. */
export const INVOICE_VIA_CUSTOMER = 'customer';
/** "Accounting — Dana Ruiz": the role it was sent through (a list that sends to a role), else the
 * contact's first role, then their name (or the name / address). A role reads by the name the
 * API gives (via_role_label / role_labels: the account's name now, after a rename), else
 * `roleLabel` (the account's roles, when the caller knows them), else the key read as words.
 * `via` is the recipient (or just its role key). */
export function invoicePersonLabel(email, name, contact, via, roleLabel) {
    const who = contact?.name || name || email;
    const named = (role, label) => role === INVOICE_VIA_CUSTOMER ? 'Customer' : label || (roleLabel?.(role) ?? contactRoleWords(role));
    const viaRole = typeof via === 'string' ? via : via?.via_role;
    let label;
    if (viaRole) {
        label = named(viaRole, typeof via === 'string' ? undefined : via?.via_role_label);
    }
    else if (contact?.roles[0]) {
        label = named(contact.roles[0], contact.role_labels?.[0]);
    }
    return label ? `${label} — ${who}` : who;
}
/** A role's name without the account's list: built in, else the account's own key read as
 * words, else the key itself (a renamed role's key keeps its first name, so prefer the API's
 * label when there is one). */
export function contactRoleWords(role) {
    return INVOICE_CONTACT_ROLE_LABELS[role] ?? customRoleWords(role) ?? role;
}
/** One of the account's own roles read as words: "custom:reconditioning_manager" → "Reconditioning manager". */
export function customRoleWords(role) {
    if (!role.startsWith('custom:'))
        return undefined;
    const words = role.slice('custom:'.length).replace(/_/g, ' ');
    return words ? words[0].toUpperCase() + words.slice(1) : undefined;
}
/** A contact's role names for the Send window: the built-in ones in their usual order, then the
 * account's own, each once. A role reads by the API's name (role_labels: the account's name now,
 * after a rename), else `roleLabel`, else the key read as words. */
export function contactRoleNames(contact, roleLabel) {
    const named = new Map();
    contact.roles.forEach((r, i) => {
        if (!named.has(r.role))
            named.set(r.role, contact.role_labels?.[i] || (roleLabel?.(r.role) ?? contactRoleWords(r.role)));
    });
    const builtIn = Object.keys(INVOICE_CONTACT_ROLE_LABELS).filter((r) => named.has(r));
    const own = [...named.keys()].filter((r) => !(r in INVOICE_CONTACT_ROLE_LABELS));
    return [...builtIn, ...own].map((r) => named.get(r));
}
/** The contact an address is, from an invoice's contacts (keys are lower-cased). */
export function invoiceContactFor(contacts, email) {
    return contacts?.[email.trim().toLowerCase()];
}
